import { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { analytics } from 'lib/analytics';

import type { EventType } from '@repo/analytics/src/Analytics';

type UserLog = {
  pattern: Pattern;
  properties?: object;
};

type AIQueryAnalyticsContext = {
  addUserLog: (userLog: UserLog) => void;
};

type Pattern =
  | 'starting_prompt_clicked'
  | 'chat_started'
  | 'chat_continued'
  | 'followup_prompt_clicked'
  | 'chat_shared'
  | 'chat_forked';

type Args = {
  event: EventType;
  properties?: object;
};

type EventSequenceRule = {
  id: string;
  pattern: Pattern[];
  analyticsEvent: EventType;
};

const EVENT_SEQUENCE_RULES: EventSequenceRule[] = [
  {
    id: 'chat-started-from-suggestion',
    pattern: ['starting_prompt_clicked', 'chat_started'],
    analyticsEvent: 'ai_query_chat_started',
  },
  {
    id: 'chat-continued-from-suggestion',
    pattern: ['followup_prompt_clicked', 'chat_continued'],
    analyticsEvent: 'ai_query_chat_continued',
  },
  {
    id: 'chat-started-custom',
    pattern: ['chat_started'],
    analyticsEvent: 'ai_query_chat_started',
  },
  {
    id: 'chat-continued',
    pattern: ['chat_continued'],
    analyticsEvent: 'ai_query_chat_continued',
  },
  {
    id: 'chat-shared',
    pattern: ['chat_shared'],
    analyticsEvent: 'ai_query_chat_shared',
  },
  {
    id: 'chat-forked',
    pattern: ['chat_forked'],
    analyticsEvent: 'ai_query_chat_forked',
  },
];

const endsWithPattern = (logs: UserLog[], pattern: Pattern[]): boolean => {
  if (logs.length < pattern.length) {
    return false;
  }

  const lastNLogs = logs.slice(-pattern.length);

  return pattern.every((eventPattern, idx) => lastNLogs[idx].pattern === eventPattern);
};

export const AIQueryAnalyticsContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [userLogs, setUserLogs] = useState<UserLog[]>([]);

  const trackEvent = useCallback(async ({ event, properties }: Args) => {
    try {
      await analytics.track(event, properties);
    } catch (e) {
      console.error('Error tracking event', e);
    }
  }, []);

  const addUserLog = (userLog: UserLog) => {
    setUserLogs(prev => [...prev, userLog]);
  };

  useEffect(() => {
    if (userLogs.length === 0) {
      return;
    }

    // Check rules in order - first match wins
    const matchedRule = EVENT_SEQUENCE_RULES.find(rule => endsWithPattern(userLogs, rule.pattern));

    if (matchedRule) {
      // Merge properties from all logs in the sequence
      const properties = userLogs.reduce(
        (acc, log) => ({
          ...acc,
          ...log.properties,
        }),
        {},
      );

      void trackEvent({
        event: matchedRule.analyticsEvent,
        properties,
      });

      setUserLogs([]);
    }
  }, [userLogs, trackEvent]);

  return <AIQueryAnalyticsContext.Provider value={{ addUserLog }}>{children}</AIQueryAnalyticsContext.Provider>;
};

const AIQueryAnalyticsContext = createContext<AIQueryAnalyticsContext | null>(null);

export const useAIQueryAnalyticsContext = () => {
  const context = useContext(AIQueryAnalyticsContext);

  if (!context) {
    throw new Error('useAIQueryAnalyticsContext must be used within a AIQueryAnalyticsContextProvider');
  }

  return context;
};
